import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { BlinqxLogo } from '../../../icons';
import { ApiDefinition } from '../../../types/api-definitions';
import { getLocalizedContent } from '../../../utils/apiUtils';
import { Language } from '../../../types/app';
import { getSpacing } from '../../../styles/theme';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

interface ProjectCardProps {
  apiDefinition: ApiDefinition;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ apiDefinition }) => {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const selectedLanguage = i18n.language as Language;

  const localizedContent = getLocalizedContent(apiDefinition, selectedLanguage);

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.default,
        boxShadow: `0 0 0 0`,
        height: 'fit-content',
        position: 'sticky',
        top: getSpacing.xl,
      }}
    >
      <CardContent
        sx={{
          padding: getSpacing.lg,
        }}
      >
        {/* Company Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: getSpacing.lg,
          }}
        >
          {apiDefinition.company.logoPath ? (
            <Box
              component='img'
              src={apiDefinition.company.logoPath}
              alt={`${apiDefinition.company.name} logo`}
              sx={{
                height: 32,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <BlinqxLogo height={32} width='auto' />
          )}
        </Box>

        {/* Short Description */}
        <Typography
          variant='body2'
          sx={{
            fontSize: 16,
            color: theme.palette.neutral.medium,
            marginBottom: getSpacing.lg,
            lineHeight: 1.5,
          }}
        >
          {localizedContent?.descriptionLong || t('projectCard.noDescription')}
        </Typography>

        {/* SLA Section */}
        {localizedContent?.sla && (
          <Box sx={{ marginBottom: getSpacing.lg }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: getSpacing.xs }}>
              <SignalCellularAltIcon
                sx={{
                  fontSize: 18,
                  marginRight: getSpacing.xs,
                  color: theme.palette.neutral.medium,
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('projectCard.slaTitle')}
              </Typography>
            </Box>
            <Typography
              variant='body2'
              sx={{
                color: theme.palette.neutral.medium,
                lineHeight: 1.5,
              }}
            >
              {localizedContent.sla}
            </Typography>
          </Box>
        )}

        {/* Contacts Section */}
        {localizedContent?.contacts && (
          <Box sx={{ marginBottom: getSpacing.lg }}>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: getSpacing.xs }}>
              <MailOutlineIcon
                sx={{
                  fontSize: 18,
                  marginRight: getSpacing.xs,
                  color: theme.palette.neutral.medium,
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('projectCard.contactsTitle')}
              </Typography>
            </Box>
            <Typography
              variant='body2'
              sx={{
                color: theme.palette.neutral.medium,
                lineHeight: 1.5,
              }}
            >
              {localizedContent.contacts}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectCard;
